import React, { FC, useCallback, useMemo, useState } from 'react';
import Style from './roleList.module.scss';
import { ColumnDef, getCoreRowModel, getGroupedRowModel, GroupingState, useReactTable } from '@tanstack/react-table';
import { Trans, useTranslation } from 'react-i18next';
import { IStudyRoleDto, Permissions, StudyRoleClaimValue } from 'src/services/api/api-client';
import { useRoleCreationModal } from './useRoleCreationModal';
import { ReactComponent as UserPlusIcon } from 'assets/img/patient/user_plus.svg';
import { ReactComponent as TooltipIcon } from 'assets/img/common/tip_filled_20.svg';
import { ReactComponent as DeleteIcon } from 'assets/img/common/cross_16_1px.svg';
import { enumToStringArr } from 'src/helpers/enum-helper';
import { AppCheckboxInput } from 'uikit/inputs';
import {
  addStudyClaimsToRole,
  removeStudyClaimsFromRole,
  removeStudyRole,
} from 'src/services/api/api-client/UserRolesClient';
import { QueryFactory } from 'src/services/api';
import { useQueryClient } from '@tanstack/react-query';
import { showServerErrorToast } from 'src/components/toast/toast-helper';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { LocalizedResourceDictionaryKeys } from 'src/application/localisation/i18next';
import { AppButton, FloatingButton } from 'uikit/buttons';
import { ConfirmResult, useConfirmationModal } from 'src/components/dialogModal/useConfirmationModal';
import { useGetStudyRolesQuery } from 'src/services/api/api-client/UserRolesQuery';
import { Loading } from 'uikit/suspense/Loading';
import { AppTableWithFixedHeader } from 'uikit/tableWithFixedHeader/tableWithFixedHeader';

export const RoleList = () => {
  const { t } = useTranslation('dev');
  const rolesQuery = useGetStudyRolesQuery({ includeForms: false });
  const queryClient = useQueryClient();

  const createRoleModal = useRoleCreationModal();
  const permissionConfirmationModal = useConfirmationModal({
    containerClassName: Style.confirmationModalContainer,
  });

  const addPermissionHandler = useCallback(
    async (roleId: string, roleName: string, permission: StudyRoleClaimValue) => {
      const result = await permissionConfirmationModal.open({
        title: t('Admin.UserRoles.AddPermissionModal.Title'),
        text: (
          <span>
            <Trans
              t={t}
              i18nKey={'Admin.UserRoles.AddPermissionModal.Text'}
              values={{
                permission: permission,
                role: roleName,
              }}
              components={{
                Bold: <span className={Style.boldText} />,
              }}
            />
          </span>
        ),
        cancelButtonText: t('Admin.UserRoles.AddPermissionModal.CancelButton'),
        okButtonText: t('Admin.UserRoles.AddPermissionModal.OkButton'),
        okButtonColorSchema: 'primary',
      });
      if (result !== ConfirmResult.Confirm) return;

      await addStudyClaimsToRole(roleId, [permission]);
      // TODO: Solve this problem in the future
      // if (
      //   permission === StudyRoleClaimValue.ApproveClosureForm &&
      //   !appliedPermissions.includes(StudyRoleClaimValue.PatientRelease)
      // ) {
      //   await addStudyClaimsToRole(roleId, [StudyRoleClaimValue.PatientRelease]);
      // }
    },
    [permissionConfirmationModal, t],
  );

  const removePermissionHandler = useCallback(
    async (roleId: string, roleName: string, permission: StudyRoleClaimValue) => {
      const result = await permissionConfirmationModal.open({
        title: t('Admin.UserRoles.RemovePermissionModal.Title'),
        text: (
          <span>
            <Trans
              t={t}
              i18nKey={'Admin.UserRoles.RemovePermissionModal.Text'}
              values={{
                permission: permission,
                role: roleName,
              }}
              components={{
                Bold: <span className={Style.boldText} />,
              }}
            />
          </span>
        ),
        cancelButtonText: t('Admin.UserRoles.RemovePermissionModal.CancelButton'),
        okButtonText: t('Admin.UserRoles.RemovePermissionModal.OkButton'),
        okButtonColorSchema: 'destroy',
      });
      if (result !== ConfirmResult.Confirm) return;

      await removeStudyClaimsFromRole(roleId, [permission]);
    },
    [permissionConfirmationModal, t],
  );

  const changePermission = useCallback(
    async (
      e: React.ChangeEvent<HTMLInputElement>,
      roleId: string,
      roleName: string,
      permission: StudyRoleClaimValue,
    ) => {
      try {
        if (e.target.checked) {
          await addPermissionHandler(roleId, roleName, permission);
        } else {
          await removePermissionHandler(roleId, roleName, permission);
        }

        await queryClient.invalidateQueries(QueryFactory.UserRolesQuery.getStudyRolesQueryKey());
      } catch (error) {
        showServerErrorToast(error);
      }
    },
    [addPermissionHandler, queryClient, removePermissionHandler],
  );

  const roles = useMemo(() => rolesQuery.data || [], [rolesQuery.data]);

  const [grouping, setGrouping] = React.useState<GroupingState>(['groupName']);
  const [columnVisibility, setColumnVisibility] = useState({
    groupName: false,
  });

  const tableData = useMemo(
    () =>
      enumToStringArr(StudyRoleClaimValue)
        .map((x) => {
          return {
            permission: x,
            groupName:
              t(`Admin.UserRoles.PermissionGroups.${permissionAndGroup[x]}` as LocalizedResourceDictionaryKeys) ??
              t('Admin.UserRoles.PermissionGroups.Other'),
            ...roles.reduce((obj, cur) => ({ ...obj, [cur.name]: cur.claims.includes(StudyRoleClaimValue[x]) }), {}),
          } satisfies RoleRow;
        })
        .sort(orderByPermissionGroup),
    [roles, t],
  );

  const rowDataTable = useReactTable<RoleRow>({
    data: tableData,
    manualSorting: false,
    initialState: {
      expanded: true,
    },
    state: {
      grouping,
      columnVisibility,
    },
    columns: useRolesColumns(roles, changePermission),
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
  });

  return (
    <>
      <div className={Style.tablePage}>
        <Loading
          loading={rolesQuery.isLoading || rolesQuery.isPreviousData}
          containerClassName={Style.loadingContainer}
          renderChildren={false}
          testId={'role-list'}
        >
          <AppTableWithFixedHeader table={rowDataTable} className={Style.table} />
        </Loading>
        <FloatingButton onClick={createRoleModal.start} Icon={UserPlusIcon} className={Style.floatingButton} />
      </div>
      {createRoleModal.modal}
      {permissionConfirmationModal.modal}
    </>
  );
};

const useRolesColumns = (
  roles: IStudyRoleDto[],
  onChangePermission: (
    e: React.ChangeEvent<HTMLInputElement>,
    roleId: string,
    roleName: string,
    permission: StudyRoleClaimValue,
  ) => Promise<void>,
): ColumnDef<RoleRow, any>[] => {
  const { t } = useTranslation('dev');

  return useMemo(
    () =>
      [
        {
          accessorKey: 'groupName',
          header: '',
          enableSorting: false,
          enableHiding: true,
          meta: {},
        },
        {
          accessorKey: 'permission',
          header: '',
          enableSorting: false,
          size: 240,
          cell: ({ row }) => {
            const permission = t(
              `Admin.UserRoles.Permissions.${row.original.permission}.ShortName` as LocalizedResourceDictionaryKeys,
            );
            const tooltipText = t(
              `Admin.UserRoles.Permissions.${row.original.permission}.Tooltip` as LocalizedResourceDictionaryKeys,
            );

            return (
              <div className={Style.permissionColumn}>
                <div className={Style.permissionText}>{permission}</div>
                <Tooltip text={tooltipText}>
                  <TooltipIcon className={Style.icon} />
                </Tooltip>
              </div>
            );
          },
        },
        ...roles?.map((x, i) => ({
          id: x.id,
          accessorKey: x.name,
          header: () => <ColumnHeader role={x} />,
          enableSorting: false,
          size: 120,
          cell: ({ row }: any) => {
            const disabled =
              row.original.permission === Permissions.RoleAssign ||
              // `Permissions.ApproveClosureForm` makes sense only with `Permissions.PatientRelease` as we automatically
              (x.claims.includes(StudyRoleClaimValue.ApproveClosureForm) &&
                row.original.claims === StudyRoleClaimValue.PatientRelease);

            return (
              <div className={Style.checkboxWrapper}>
                <AppCheckboxInput
                  disabled={disabled}
                  checked={row.original[x.name]}
                  onChange={(e) => onChangePermission(e, x.id, x.name, row.original.permission)}
                />
              </div>
            );
          },
          meta: {
            isColored: !(i % 2),
          },
        })),
      ] satisfies ColumnDef<RoleRow, any>[],
    [onChangePermission, roles, t],
  );
};

const ColumnHeader: FC<{ role: IStudyRoleDto }> = ({ role }) => {
  const { t } = useTranslation('dev');
  const queryClient = useQueryClient();
  const confirmationModal = useConfirmationModal();

  const onDelete = useCallback(async () => {
    const result = await confirmationModal.open({
      title: t('Admin.UserRoles.RemoveRoleModal.Title'),
      text: (
        <span>
          <Trans
            t={t}
            i18nKey={'Admin.UserRoles.RemoveRoleModal.Text'}
            values={{
              roleName: role.name,
            }}
            components={{
              Bold: <span className={Style.boldText} />,
            }}
          />
        </span>
      ),
      cancelButtonText: t('Admin.UserRoles.RemoveRoleModal.CancelButton'),
      okButtonText: t('Admin.UserRoles.RemoveRoleModal.OkButton'),
      okButtonColorSchema: 'destroy',
    });

    if (result === ConfirmResult.Confirm) {
      try {
        await removeStudyRole(role.id);
        await queryClient.invalidateQueries(QueryFactory.UserRolesQuery.getStudyRolesQueryKey());
      } catch (error) {
        showServerErrorToast(error);
      }
    }
  }, [confirmationModal, queryClient, role.id, role.name, t]);

  return (
    <>
      <div className={Style.customHeader}>
        <div>{role.name}</div>
        <AppButton
          variant={'icon-link'}
          colorSchema={'basic'}
          Icon={DeleteIcon}
          onClick={onDelete}
          iconClassName={Style.deleteIcon}
        />
      </div>
      {confirmationModal.modal}
    </>
  );
};

export const PermissionGroups = [
  'Study',
  'User',
  'Patient',
  'Issue',
  'Analysis',
  'Approve',
  'Export',
  'Participants',
  'Ue Form',
  'Monitoring',
  'Other',
] as const;
export type PermissionGroup = (typeof PermissionGroups)[number];

const permissionAndGroup: { [key in keyof typeof StudyRoleClaimValue]: PermissionGroup } = {
  StudyConfigure: 'Study',
  CloseStudy: 'Study',
  StudyInfoPageView: 'Study',
  StudyUserListView: 'Study',

  PatientView: 'Patient',
  PatientCreate: 'Patient',
  PatientRelease: 'Patient',
  PatientArchive: 'Patient',
  PatientReviewApproval: 'Patient',

  IssueView: 'Issue',
  IssueCreate: 'Issue',
  IssueProcess: 'Issue',
  IssueApprove: 'Issue',
  IssueDiscuss: 'Issue',

  AnalysisPresetView: 'Analysis',
  AnalysisPresetManage: 'Analysis',
  AnalysisView: 'Analysis',
  AnalysisManage: 'Analysis',

  ApproveForm: 'Approve',
  ApproveClosureForm: 'Approve',
  ApproveClosureStudy: 'Approve',

  ExportForm: 'Export',
  ExportPopulation: 'Export',
  ExportPatientEcrfReport: 'Export',
  ExportAuditLog: 'Export',

  ParticipantsView: 'Participants',
  ParticipantsEdit: 'Participants',
  ParticipantsApprove: 'Participants',

  UeFormCreate: 'Ue Form',
  UeFormEdit: 'Ue Form',

  MonitoringReportView: 'Monitoring',
  MonitoringReportCreate: 'Monitoring',
};

type RoleRow = {
  groupName: PermissionGroup;
  permission: string;
};

const orderByPermissionGroup = (a: RoleRow, b: RoleRow) => {
  if (PermissionGroups.indexOf(a.groupName) < PermissionGroups.indexOf(b.groupName)) return -1;
  if (PermissionGroups.indexOf(a.groupName) > PermissionGroups.indexOf(b.groupName)) return 1;
  return 0;
};
